<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import TasksAPI from 'dashboard/api/captain/tasks';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['select']);

const suggestions = ref([]);
const isLoading = ref(false);
let abortController = null;

// Lets the parent keep Space a plain keystroke while a fetch is in flight or
// chips are already on screen.
const isBusy = computed(() => isLoading.value || suggestions.value.length > 0);

const reset = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  suggestions.value = [];
  isLoading.value = false;
};

const fetchSuggestions = async () => {
  // A fetch is already in flight or chips are already on screen.
  if (isLoading.value || suggestions.value.length) return;

  const requestController = new AbortController();
  abortController = requestController;
  isLoading.value = true;

  try {
    const {
      data: { message },
    } = await TasksAPI.replySuggestion(
      props.conversationId,
      requestController.signal
    );
    if (requestController.signal.aborted) return;
    // Show nothing on an empty response; the row falls back to the trigger.
    suggestions.value = message ? [message] : [];
  } catch (error) {
    // Show nothing on error.
    suggestions.value = [];
  } finally {
    if (abortController === requestController) {
      abortController = null;
      isLoading.value = false;
    }
  }
};

const onSelect = suggestion => {
  emit('select', suggestion);
  suggestions.value = [];
};

watch(() => props.conversationId, reset);
onBeforeUnmount(reset);

defineExpose({ fetchSuggestions, isBusy });
</script>

<template>
  <div class="flex items-center min-w-0 gap-2">
    <div
      v-if="isLoading"
      class="max-w-full rounded-full h-7 w-36 bg-n-alpha-2 animate-pulse"
    />
    <template v-else-if="suggestions.length">
      <button
        v-for="suggestion in suggestions"
        :key="suggestion"
        type="button"
        :title="suggestion"
        class="inline-flex items-center h-7 min-w-0 px-3 text-xs transition-colors rounded-full bg-n-violet-3 text-n-violet-11 hover:bg-n-violet-4"
        @click="onSelect(suggestion)"
      >
        <span class="truncate">{{ suggestion }}</span>
      </button>
    </template>
    <button
      v-else
      type="button"
      :title="$t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.SUGGESTION')"
      class="inline-flex items-center h-7 min-w-0 gap-1 px-2.5 text-xs transition-colors rounded-full bg-n-violet-3 text-n-violet-11 hover:bg-n-violet-4"
      @click="fetchSuggestions"
    >
      <span class="flex-shrink-0 i-ph-sparkle size-3.5" />
      <span class="truncate">{{ $t('CONVERSATION.REPLYBOX.SUGGEST') }}</span>
    </button>
  </div>
</template>
